import { useContext, useEffect, useMemo, useState } from "react";
import PopUp from "../PopUp";
import api from "../api/api";
import { AppContext } from "../../App";
import Table from "../ui/Table";
import PencilSquareIcon from "../../icons/PencilSquareIcon";
import TrashCanIcon from "../../icons/TrashCanIcon";

export default function ListCategories() {
	const {
		setEditCategoriesPopupVisible,
		setListProductsPopupVisible,
		setConfirm,
		setLoading,
		setListCategoriesPopupVisible,
		config,
		loadCategories,
		categories,
		loadMenu,
	} = useContext(AppContext);
	const [orderedCategories, setOrderedCategories] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		loadCategories();
	}, []);

	const deleteElement = async (id) => {
		setConfirm({
			title: "Sei sicuro?",
			onConfirm: async () => {
				setLoading(true);
				const res = await api.postProtected("/categories/delete/" + id + "/");
				setLoading(false);
				if (res.success == 0)
					setConfirm({
						title: "Errore",
						message: res.error,
					});
				else {
					await loadCategories();
					if (typeof loadMenu === "function") await loadMenu();
				}
			},
		});
	};

	const setCategoryActivePos = async (category, value) => {
		setLoading(true);
		const res = await api.postProtected("/categories/edit/" + category.id + "/", {
			active_pos: value,
			active: value,
		});
		setLoading(false);
	if (res.success == 0)
		setConfirm({
			title: "Errore",
			message: res.error,
		});
	else {
		await loadCategories();
		if (typeof loadMenu === "function") await loadMenu();
	}
};

	const sortedCategories = useMemo(
		() =>
			[...categories].sort((a, b) => {
				const aPos = Number.isFinite(Number(a?.position)) ? Number(a.position) : 999999;
				const bPos = Number.isFinite(Number(b?.position)) ? Number(b.position) : 999999;
				if (aPos !== bPos) return aPos - bPos;
				return String(a?.name || "").localeCompare(String(b?.name || ""), "it", {
					sensitivity: "base",
				});
			}),
		[categories]
	);

	useEffect(() => {
		setOrderedCategories(sortedCategories);
	}, [sortedCategories]);

	const normalizedSearchTerm = searchTerm.trim().toLowerCase();
	const isSearchMode = normalizedSearchTerm.length > 0;
	const visibleCategories = useMemo(() => {
		if (!isSearchMode) return orderedCategories;
		return orderedCategories.filter((category) =>
			String(category?.name || "")
				.toLowerCase()
				.includes(normalizedSearchTerm)
		);
	}, [orderedCategories, isSearchMode, normalizedSearchTerm]);

	const reorderRows = (rows, draggedRow, targetRow) => {
		const draggedId = Number(draggedRow?.id);
		const targetId = Number(targetRow?.id);
		if (!draggedId || !targetId || draggedId === targetId) return rows;
		const nextRows = [...rows];
		const fromIndex = nextRows.findIndex((row) => Number(row.id) === draggedId);
		const toIndex = nextRows.findIndex((row) => Number(row.id) === targetId);
		if (fromIndex < 0 || toIndex < 0) return rows;
		const [moved] = nextRows.splice(fromIndex, 1);
		nextRows.splice(toIndex, 0, moved);
		return nextRows;
	};

	const categoryPositionsMap = useMemo(() => {
		const map = {};
		for (const category of categories) map[Number(category.id)] = Number(category.position);
		return map;
	}, [categories]);

	const reorderCategories = async (draggedCategory, targetCategory) => {
		const reordered = reorderRows(orderedCategories, draggedCategory, targetCategory);
		if (reordered === orderedCategories) return;
		setOrderedCategories(reordered);
		try {
			const updates = reordered
				.map((category, index) => ({
					id: category.id,
					position: index + 1,
				}))
				.filter((update) => categoryPositionsMap[Number(update.id)] !== update.position);

			await Promise.all(
				updates.map((update) =>
					api.postProtected("/categories/edit/" + update.id + "/", {
						position: update.position,
					})
				)
			);
			await loadCategories(false);
			if (typeof loadMenu === "function") await loadMenu();
		} catch {
			setOrderedCategories(sortedCategories);
			setConfirm({
				title: "Errore",
				message: "Errore durante il riordino delle categorie",
			});
		}
	};

	return (
		<PopUp
			size="large"
			options={{
				title: "Categorie",
				headerButtons: (
					<>
						<button
							className="popup-header-add-button"
							onClick={() => {
								setEditCategoriesPopupVisible(true);
							}}
						>
							Aggiungi Categoria
						</button>
						<button
							className="popup-header-add-button"
							onClick={() => {
								setListProductsPopupVisible(true);
							}}
						>
							Aggiungi Prodotto
						</button>
					</>
				),
				onClose: () => setListCategoriesPopupVisible(false),
			}}
		>
			<div className="inner-content">
				<div className="categories-admin-content">
						<div className="admin-list-search">
							<input
								type="text"
							placeholder="Cerca categoria"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
						{searchTerm && (
							<button type="button" onClick={() => setSearchTerm("")}>
								&times;
								</button>
							)}
						</div>
						{isSearchMode && visibleCategories.length === 0 && (
							<div className="admin-search-empty mb-2">nessuna categoria trovata</div>
						)}
						<div className="categories-admin-table">
						<Table
							header={true}
							config={config}
							draggableRows={!isSearchMode}
							onRowReorder={reorderCategories}
							cols={[
								{ id: "id", label: "ID", type: "id", align: "center", width: 0.6 },
								{ id: "name", label: "Nome", type: "text", align: "left", width: 2.6 },
									{
										id: "active_pos",
										label: "In Vendita",
										type: "toggle",
										onChange: (category, value) => setCategoryActivePos(category, value),
										align: "center",
										width: 1.2,
								},
								{
									id: "buttons",
									label: "",
									type: "buttons",
									align: "right",
									width: 1,
										buttons: [
											{
												icon: <PencilSquareIcon />,
												onClick: (category) =>
													setEditCategoriesPopupVisible(category.id),
												visible: true,
											},
											{
												icon: <TrashCanIcon />,
												onClick: (category) => deleteElement(category.id),
												className: "button-2",
												visible: true,
											},
										],
									},
							]}
							rows={visibleCategories}
						/>
					</div>
				</div>
			</div>
		</PopUp>
	);
}
